import React, { useState, useEffect, useMemo } from "react";
import { Card, Spinner, Collapse, Button } from "reactstrap";
import { useQueryClient } from "@tanstack/react-query";
import classnames from "classnames";
import ButtonIcon from "../Buttons/ButtonIcon";
import OffCanvas from "../OffCanvas/OffCanvas";
import ModalDelete from "../Modal/ModalDelete";
import DeleteFaqForm from "./DeleteFaqForm";
import { BiEdit, BiRefresh } from "react-icons/bi";
import { MdDelete } from "react-icons/md";
import AddFAQ from "./AddFAQ";
import EditFaq from "./EditFaq";
import { dangerMessage, successMessage } from "../../components/Toast/Toast";

function Faq({ token, eventDetails }) {
  const [faqID, setFaqID] = useState(null);
  const [collapseID, setCollapseID] = useState(null);
  const [isFaqOffcanvasOpen, setFaqIsOffcanvasOpen] = useState(false);
  const [isFaqEditOffcanvasOpen, setFaqEditIsOffcanvasOpen] = useState(false);
  const [isToggleDelete, setIsToggleDelete] = useState(false);

  const queryClient = useQueryClient();
  
  // ✅ Get eventID
  const eventID = eventDetails?._id || eventDetails?.id;
  
  // ✅ STABLE query key with useMemo - prevents infinite re-renders
  const queryKey = useMemo(() => {
    return eventID ? ["myEventDetail", eventID] : ["myEventDetail"];
  }, [eventID]);

  const toggleAddFAQ = () => setFaqIsOffcanvasOpen(!isFaqOffcanvasOpen);
  const toggleEditFAQ = () => setFaqEditIsOffcanvasOpen(!isFaqEditOffcanvasOpen);

  // ✅ Simple refresh using queryClient - no custom mutation needed
  const handleRefresh = async () => {
    if (!eventID || !token) {
      dangerMessage("Missing credentials. Cannot refresh.");
      return;
    }
    
    try {
      console.log("🔄 Refreshing...");
      
      // ✅ Refetch with stable queryKey
      await queryClient.refetchQueries({
        queryKey: queryKey,
        exact: true,
        type: 'all'
      });
      
      successMessage("FAQs updated");
    } catch (error) {
      console.error("Refresh error:", error);
      dangerMessage("Could not refresh. Check connection.");
    }
  };

  const handleFaqAdded = () => {
    handleRefresh();
  };

  const handleDeleteFaq = (id) => {
    if (!id) return;
    setFaqID(id);
    setIsToggleDelete(true);
  };

  const handleEditFaq = (id) => {
    if (!id) return;
    setFaqID(id);
    setFaqEditIsOffcanvasOpen(true);
  };

  const handleCollapse = (id) => {
    setCollapseID(collapseID === id ? null : id);
  };

  useEffect(() => {
    if (collapseID && eventDetails?.faq) {
      const faqExists = eventDetails.faq.some(faq => faq?._id === collapseID);
      if (!faqExists) setCollapseID(null);
    }
  }, [eventDetails?.faq, collapseID]);

  useEffect(() => {
    if (!isToggleDelete && faqID) setFaqID(null);
  }, [isToggleDelete, faqID]);

  return (
    // ✅ RESPONSIVE CONTAINER - removed fixed width & margin
    <div className="w-100 px-2 px-md-3 px-lg-4" style={{ maxWidth: "900px", margin: "0 auto" }}>
      <div className="w-100">
        
        {/* ✅ RESPONSIVE BUTTON ROW - flex-wrap for mobile */}
        <div className="m-2 d-flex flex-wrap justify-content-end align-items-center gap-2">
          
          {/* ✅ REFRESH BUTTON - responsive sizing */}
          <Button
            onClick={handleRefresh}
            disabled={!eventID || !token}
            outline
            color="secondary"
            className="d-flex align-items-center gap-2 px-3 flex-shrink-0"
            style={{
              borderRadius: "25px",
              fontWeight: "500",
              fontSize: "13px",
              minWidth: "110px",
              padding: "8px 16px"
            }}
            title="Refresh FAQ list"
          >
            <BiRefresh size={16} />
            <span className="d-none d-sm-inline">Refresh</span>
          </Button>
          
          <ButtonIcon
            Title="Add FAQ"
            BackgroundColor="#f68a20"
            ColorText="#fff"
            BorderColor="#f68a20"
            borderRadius={50}
            handleOnclick={toggleAddFAQ}
            Icon={<></>}
            pending={false}
          />
        </div>

        {/* ✅ RESPONSIVE ACCORDION */}
        <div className="accordion accordion-flush p-2 p-md-3">
          {eventDetails?.faq && eventDetails.faq.length > 0 ? (
            eventDetails.faq.map((faq, index) => (
              <Card key={faq?._id || index} className="mb-2">
                <div className="accordion-item px-2 py-2">
                  <h2 className="accordion-header" id={`headingFlush${faq?._id}`}>
                    {/* ✅ FLEX WRAP for mobile - actions wrap below question */}
                    <div className="d-flex flex-wrap gap-1 align-items-center">
                      <button
                        className={classnames("accordion-button", "fw-medium", "flex-grow-1", {
                          collapsed: faq?._id !== collapseID,
                        })}
                        type="button"
                        onClick={() => handleCollapse(faq?._id)}
                        style={{ 
                          cursor: "pointer",
                          fontSize: "14px",
                          padding: "8px 12px",
                          textAlign: "left",
                          whiteSpace: "normal",
                          wordBreak: "break-word"
                        }}
                      >
                        {faq?.question}
                      </button>
                      
                      {/* ✅ Action buttons - grouped and responsive */}
                      <div className="d-flex gap-1 align-items-center">
                        <div 
                          onClick={() => handleEditFaq(faq?._id)} 
                          style={{ cursor: "pointer", padding: "4px" }}
                          className="p-1"
                        >
                          <BiEdit color="black" size={18} />
                        </div>
                        <div 
                          onClick={() => handleDeleteFaq(faq?._id)} 
                          style={{ cursor: "pointer", padding: "4px" }}
                          className="p-1"
                        >
                          <MdDelete color="red" size={18} />
                        </div>
                      </div>
                    </div>
                  </h2>
                  <Collapse isOpen={faq?._id === collapseID} className="accordion-collapse">
                    <div className="accordion-body">
                      <div className="text-muted" style={{ fontSize: "14px", lineHeight: "1.5" }}>
                        {faq?.answer}
                      </div>
                    </div>
                  </Collapse>
                </div>
              </Card>
            ))
          ) : (
            <div className="text-center text-muted p-4">
              No FAQs available. Click "Add FAQ" to create one.
            </div>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      <ModalDelete
        size="md"
        isOpen={isToggleDelete}
        toggle={() => setIsToggleDelete(false)}
        title="Delete Message"
        body={
          <DeleteFaqForm
            isToggleDelete={isToggleDelete}
            setIsToggleDelete={setIsToggleDelete}
            faqID={faqID}
            token={token}
            eventID={eventDetails?._id}
          />
        }
      />

      {/* ✅ Add FAQ OffCanvas - with responsive class */}
      <OffCanvas
        isOffcanvasOpen={isFaqOffcanvasOpen}
        title="Add FAQ"
        body={
          <AddFAQ
            eventID={eventDetails?._id}
            token={token}
            toggleAddFAQ={toggleAddFAQ}
            onFaqAdded={handleFaqAdded}
          />
        }
        toggleSaveChanges={toggleAddFAQ}
        directionOffCanvas="end"
        widthOffCanvas={400}
      />
      
      {/* ✅ Edit FAQ OffCanvas - with responsive class */}
      <OffCanvas
        isOffcanvasOpen={isFaqEditOffcanvasOpen}
        title="Edit FAQ"
        body={
          <EditFaq
            eventID={eventDetails?._id}
            faqID={faqID}
            token={token}
            toggleEditFAQ={toggleEditFAQ}
          />
        }
        toggleSaveChanges={toggleEditFAQ}
        directionOffCanvas="end"
        widthOffCanvas={400}
      />
    </div>
  );
}

export default Faq;